import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Paperclip, Send, Loader2, X, FileText, FileSpreadsheet, Check, CheckCheck, Users, AtSign, MessageCircle, Trash2, Ban, Download, Search, ChevronUp, ChevronDown } from "lucide-react";
import { GroupMembersDialog } from "./corr/GroupMembersDialog";
import { exportChatPdf } from "../lib/chatExport";

export const timeOnly = (iso) => (iso ? new Date(iso).toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" }) : "");
export const listTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), n = new Date();
  if (d.toDateString() === n.toDateString()) return timeOnly(iso);
  const y = new Date(n); y.setDate(n.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "أمس";
  return d.toLocaleDateString("en-GB");
};
const dayLabel = (iso) => {
  const d = new Date(iso), n = new Date();
  if (d.toDateString() === n.toDateString()) return "اليوم";
  const y = new Date(n); y.setDate(n.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "أمس";
  return d.toLocaleDateString("ar-SA", { weekday: "long", day: "numeric", month: "long" });
};
export const initials = (n) => (n || "؟").trim().charAt(0);
const COLORS = ["bg-emerald-600", "bg-sky-600", "bg-amber-600", "bg-rose-600", "bg-violet-600", "bg-teal-600", "bg-orange-600"];
const TEXTS = ["text-emerald-700", "text-sky-700", "text-amber-700", "text-rose-700", "text-violet-700", "text-teal-700", "text-orange-700"];
const hash = (s) => [...(s || "")].reduce((a, c) => a + c.charCodeAt(0), 0);
export const colorFor = (s) => COLORS[hash(s) % COLORS.length];
const textFor = (s) => TEXTS[hash(s) % TEXTS.length];
export const Avatar = ({ name, group, size = "h-11 w-11" }) => (
  <div className={`${size} shrink-0 rounded-full ${group ? "bg-slate-400" : colorFor(name)} flex items-center justify-center text-white font-bold`}>
    {group ? <Users className="h-5 w-5" /> : initials(name)}
  </div>
);

export const useUploads = (upload) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try { for (const f of files) { const r = await upload(f); setItems((p) => [...p, { id: r.attachment_id, name: r.attachment_name, content_type: r.content_type }]); } }
    catch (err) { toast.error(err?.response?.data?.detail || "تعذّر رفع الملف"); }
    finally { setBusy(false); }
  };
  const node = <input ref={ref} type="file" multiple hidden onChange={onFiles} accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.xlsx,.xls,.csv,.doc,.docx" data-testid="corr-file-input" />;
  return { items, busy, pick: () => ref.current?.click(), remove: (id) => setItems((p) => p.filter((x) => x.id !== id)), reset: () => setItems([]), node };
};

const Att = ({ a, urlFor }) => (a.content_type || "").includes("image") ? (
  <a href={urlFor(a.id)} target="_blank" rel="noreferrer" data-testid={`corr-att-${a.id}`}><img src={urlFor(a.id)} alt={a.name} className="mt-1 max-h-56 rounded-lg object-cover" /></a>
) : (
  <a href={urlFor(a.id)} target="_blank" rel="noreferrer" data-testid={`corr-att-${a.id}`}
    className="mt-1 flex items-center gap-2 rounded-lg bg-black/5 px-2.5 py-2 text-xs text-slate-700 hover:bg-black/10">
    {(a.content_type || "").match(/sheet|excel|csv/) ? <FileSpreadsheet className="h-5 w-5 text-emerald-600" /> : <FileText className="h-5 w-5 text-red-500" />}
    <span className="truncate max-w-[200px]">{a.name}</span>
  </a>
);

const Body = ({ text, mentions }) => {
  if (!mentions?.length) return <span>{text}</span>;
  const names = mentions.map((m) => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
  const re = new RegExp(`(@(?:${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}))`, "g");
  return text.split(re).map((p, i) => (p.startsWith("@") && names.includes(p.slice(1))
    ? <span key={i} className="font-bold text-sky-700">{p}</span> : <span key={i}>{p}</span>));
};

const fmtRead = (at) => { try { return new Date(at).toLocaleString("ar-SA-u-nu-latn-ca-gregory", { dateStyle: "short", timeStyle: "short" }); } catch { return at; } };

const Bubble = ({ m, mine, showName, read, readers, urlFor, onWa, onDel }) => (
  <div className={`group flex ${mine ? "justify-end" : "justify-start"} px-2`} data-testid={`corr-msg-${m.id}`}>
    <div className={`relative max-w-[82%] sm:max-w-[65%] rounded-xl px-2.5 pt-1.5 pb-1 shadow-sm ${mine ? "bg-[#d9fdd3] rounded-tl-sm" : "bg-white rounded-tr-sm"}`}>
      {showName && !mine && <div className={`text-[12px] font-bold ${textFor(m.sender_name)}`}>{m.sender_name}</div>}
      {m.deleted ? <p className="flex items-center gap-1 text-[13px] italic text-slate-400" data-testid={`corr-msg-deleted-${m.id}`}><Ban className="h-3.5 w-3.5" /> تم حذف هذه الرسالة</p> : <>
        {m.attachments?.map((a) => <Att key={a.id} a={a} urlFor={urlFor} />)}
        {m.body && <p className="whitespace-pre-wrap break-words text-[14.5px] leading-relaxed text-slate-800"><Body text={m.body} mentions={m.mentions} /></p>}
      </>}
      <div className="flex items-center justify-end gap-1 text-[10.5px] text-slate-500 -mt-0.5">
        {onDel && !m.deleted && <button type="button" title="حذف الرسالة" onClick={() => onDel(m)} data-testid={`corr-msg-del-${m.id}`} className="rounded p-0.5 text-slate-400 opacity-60 transition-opacity hover:bg-rose-100 hover:text-rose-600 group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>}
        {onWa && !m.deleted && <button type="button" title="إرسال عبر واتساب" onClick={() => onWa(m)} data-testid={`corr-msg-wa-${m.id}`} className="rounded p-0.5 text-emerald-600 hover:bg-emerald-100"><MessageCircle className="h-3.5 w-3.5" /></button>}
        {timeOnly(m.created_at)}
        {m.via === "whatsapp" && <span className="rounded bg-emerald-100 px-1 text-[9.5px] font-bold text-emerald-700" data-testid={`corr-msg-via-wa-${m.id}`}>واتساب</span>}
        {mine && (read ? <CheckCheck className="h-3.5 w-3.5 text-sky-500" data-testid="corr-tick-read" /> : <Check className="h-3.5 w-3.5" data-testid="corr-tick-sent" />)}
      </div>
      {mine && readers?.length > 0 && <div className="mt-0.5 border-t border-emerald-200/70 pt-0.5 text-[10.5px] text-sky-700" data-testid={`corr-read-by-${m.id}`}>قرأها: {readers.map((x) => `${x.name || "—"} (${fmtRead(x.at)})`).join("، ")}</div>}
    </div>
  </div>
);

// WhatsApp-like live conversation. adapter: { load(tid, since), send(tid, body), upload(file), urlFor(id) }
const ActAsBar = ({ parts, value, onChange }) => {
  const chip = (id, label) => (
    <button key={id || "me"} type="button" onClick={() => onChange(id)} data-testid={`corr-act-as-${id || "me"}`}
      className={`rounded-full border px-3 py-1 text-xs font-bold transition-colors ${value === id ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"}`}>{label}</button>
  );
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="corr-act-as-bar">
      <MessageCircle className="h-4 w-4 text-slate-400" />
      {chip("", "أنا")}
      {parts.length <= 4 ? parts.map((p) => chip(p.id, p.name)) : (
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="corr-act-as-select"
          className={`rounded-full border px-3 py-1 text-xs font-bold ${value ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600"}`}>
          <option value="">اختر شخص…</option>{parts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>)}
    </div>
  );
};

export const ChatRoom = ({ threadId, adapter, title, subtitle, group, onBack, onChanged, onWa, canDeleteAny, onDeleteThread, company, actAs }) => {
  const [asParty, setAsParty] = useState("");
  const [showMembers, setShowMembers] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [reads, setReads] = useState(new Set());
  const [readInfo, setReadInfo] = useState({});
  const [parts, setParts] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [mq, setMq] = useState(null);
  const [sending, setSending] = useState(false);
  const up = useUploads(adapter.upload);
  const endRef = useRef(null), taRef = useRef(null), lastRef = useRef("");

  const pull = useCallback(async (full) => {
    try {
      const r = await adapter.load(threadId, full ? "" : lastRef.current);
      if (full) { setParts(r.participants || []); setMe(r.me); }
      setReads(new Set(r.read_ids || []));
      setReadInfo(r.read_info || {});
      const del = new Set(r.deleted_ids || []);
      if (del.size) setMsgs((p) => (p.some((x) => del.has(x.id) && !x.deleted) ? p.map((x) => (del.has(x.id) ? { ...x, deleted: true, body: "", attachments: [] } : x)) : p));
      if (r.messages?.length) {
        lastRef.current = r.messages[r.messages.length - 1].created_at;
        setMsgs((p) => { const ids = new Set(p.map((x) => x.id)); return full ? r.messages : [...p, ...r.messages.filter((x) => !ids.has(x.id))]; });
        if (!full) onChanged?.();
      }
    } catch { if (full) toast.error("تعذّر تحميل المحادثة"); }
    finally { if (full) setLoading(false); }
  }, [threadId, adapter, onChanged]);

  useEffect(() => { lastRef.current = ""; setMsgs([]); setLoading(true); pull(true); const iv = setInterval(() => { if (!document.hidden) pull(false); }, 2500); return () => clearInterval(iv); }, [pull]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs.length]);

  const cands = useMemo(() => (mq === null ? [] : parts.filter((p) => p.id !== me && (p.name || "").includes(mq)).slice(0, 8)), [mq, parts, me]);
  const onType = (e) => {
    const v = e.target.value; setText(v);
    const m = v.slice(0, e.target.selectionStart).match(/(?:^|\s)@([^\s@]*)$/);
    setMq(m ? m[1] : null);
  };
  const pickMention = (p) => {
    const ta = taRef.current, pos = ta?.selectionStart ?? text.length;
    const before = text.slice(0, pos).replace(/@([^\s@]*)$/, `@${p.name} `);
    setText(before + text.slice(pos)); setMq(null);
    setMentions((x) => (x.some((y) => y.id === p.id) ? x : [...x, { id: p.id, name: p.name }]));
    setTimeout(() => ta?.focus(), 0);
  };
  const send = async () => {
    if (!text.trim() && !up.items.length) return;
    setSending(true);
    try {
      await adapter.send(threadId, { body: text.trim(), attachments: up.items, mentions: mentions.filter((m) => text.includes(`@${m.name}`)), ...(asParty ? { as_party: asParty } : {}) });
      setText(""); setMentions([]); up.reset(); await pull(false); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setSending(false); }
  };

  const delMsg = async (m) => {
    if (!window.confirm("حذف هذه الرسالة عند الجميع؟")) return;
    try { await adapter.del(m.id); setMsgs((p) => p.map((x) => (x.id === m.id ? { ...x, deleted: true, body: "", attachments: [] } : x))); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  const exportPdf = () => { if (!exportChatPdf({ title, subtitle: subtitle || parts.map((p) => p.name).join("، "), msgs, me, company })) toast.error("اسمح بالنوافذ المنبثقة لتصدير PDF"); };
  const [sq, setSq] = useState(null);
  const [si, setSi] = useState(0);
  const hits = useMemo(() => { const k = (sq || "").trim().toLowerCase(); return k ? msgs.filter((m) => !m.deleted && `${m.body || ""} ${(m.attachments || []).map((a) => a.name).join(" ")} ${m.sender_name || ""}`.toLowerCase().includes(k)).map((m) => m.id) : []; }, [sq, msgs]);
  const hitId = hits.length ? hits[Math.min(si, hits.length - 1)] : null;
  useEffect(() => { setSi(hits.length ? hits.length - 1 : 0); }, [sq]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (hitId) document.querySelector(`[data-testid="corr-msg-${hitId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [hitId]);

  let lastDay = "";
  return (
    <div className="flex h-full flex-col" data-testid="corr-conversation">
      <div className="flex items-center gap-3 bg-[#f0f2f5] px-3 py-2.5 border-b border-slate-200">
        {onBack && <button onClick={onBack} className="rounded-full p-1.5 hover:bg-slate-200 lg:hidden" data-testid="corr-back-button"><ArrowRight className="h-5 w-5 text-slate-600" /></button>}
        <Avatar name={title} group={group} size="h-10 w-10" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-bold text-slate-900" data-testid="corr-room-title">{title}</h3>
          <p className="truncate text-[11.5px] text-slate-500">{subtitle || parts.map((p) => p.name).join("، ")}</p>
        </div>
        {onWa && <button type="button" title="إرسال واتساب للموظفين" onClick={() => onWa(null, parts)} data-testid="corr-room-wa" className="rounded-full p-2 text-emerald-600 hover:bg-emerald-100"><MessageCircle className="h-5 w-5" /></button>}
        {group && (actAs || parts.find((p) => p.id === me)?.admin) && <button type="button" title="الأعضاء" onClick={() => setShowMembers(true)} data-testid="corr-members-btn" className="rounded-full p-2 text-slate-600 hover:bg-slate-200"><Users className="h-5 w-5" /></button>}
        {showMembers && <GroupMembersDialog threadId={threadId} parts={parts} me={me} isTop={!!actAs} onClose={() => setShowMembers(false)} onChanged={() => pull(true)} />}
        <button type="button" title="بحث في المحادثة" onClick={() => setSq(sq === null ? "" : null)} data-testid="corr-room-search-toggle" className={`rounded-full p-2 hover:bg-slate-200 ${sq !== null ? "bg-slate-200 text-emerald-700" : "text-slate-600"}`}><Search className="h-5 w-5" /></button>
        <button type="button" title="تصدير المحادثة PDF" onClick={exportPdf} data-testid="corr-room-export" className="flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200"><Download className="h-4 w-4" /> PDF</button>
        {onDeleteThread && <button type="button" title="حذف المحادثة بالكامل" onClick={onDeleteThread} data-testid="corr-room-delete" className="rounded-full p-2 text-rose-500 hover:bg-rose-50"><Trash2 className="h-5 w-5" /></button>}
      </div>
      {sq !== null && (
        <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2" data-testid="corr-room-search-bar">
          <Search className="h-4 w-4 text-slate-400" />
          <input autoFocus value={sq} onChange={(e) => setSq(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setSi((i) => (hits.length ? (i - 1 + hits.length) % hits.length : 0)); if (e.key === "Escape") setSq(null); }}
            placeholder="ابحث داخل المحادثة…" className="flex-1 bg-transparent text-sm outline-none" data-testid="corr-room-search-input" />
          <span className="font-mono-nums text-xs text-slate-500" data-testid="corr-room-search-count">{sq.trim() ? (hits.length ? `${Math.min(si, hits.length - 1) + 1}/${hits.length}` : "0") : ""}</span>
          <button type="button" disabled={!hits.length} onClick={() => setSi((i) => (i - 1 + hits.length) % hits.length)} title="الأقدم" data-testid="corr-room-search-prev" className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-40"><ChevronUp className="h-4 w-4" /></button>
          <button type="button" disabled={!hits.length} onClick={() => setSi((i) => (i + 1) % hits.length)} title="الأحدث" data-testid="corr-room-search-next" className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-40"><ChevronDown className="h-4 w-4" /></button>
          <button type="button" onClick={() => setSq(null)} data-testid="corr-room-search-close" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
      )}
      <div className="flex-1 overflow-y-auto py-3 space-y-1 bg-[#efeae2] [background-image:radial-gradient(rgba(0,0,0,0.035)_1px,transparent_1px)] [background-size:14px_14px]">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          : msgs.length === 0 ? <p className="mx-auto mt-10 w-fit rounded-lg bg-[#fff5c4] px-3 py-1.5 text-xs text-slate-600">ابدأ المحادثة بأول رسالة 👋</p>
          : msgs.map((m, i) => {
            const d = dayLabel(m.created_at); const sep = d !== lastDay; lastDay = d;
            const mine = m.sender_id === me;
            return (
              <div key={m.id} className={hitId === m.id ? "rounded-lg bg-amber-200/60 ring-2 ring-amber-400 transition-colors" : hits.includes(m.id) ? "bg-amber-100/40" : ""}>
                {sep && <div className="my-2 flex justify-center"><span className="rounded-lg bg-white/90 px-3 py-1 text-[11.5px] text-slate-500 shadow-sm">{d}</span></div>}
                <Bubble m={m} mine={mine} read={reads.has(m.id)} readers={readInfo[m.id]} urlFor={adapter.urlFor} onWa={onWa && ((x) => onWa(x, parts))} onDel={adapter.del && (mine || canDeleteAny) ? delMsg : undefined} showName={i === 0 || msgs[i - 1].sender_id !== m.sender_id || !!sep} />
              </div>
            );
          })}
        <div ref={endRef} />
      </div>
      <div className="relative bg-[#f0f2f5] px-2 py-2 space-y-1.5">
        {cands.length > 0 && (
          <div className="absolute bottom-full right-2 left-2 mb-1 max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="corr-mention-list">
            {cands.map((p) => (
              <button key={p.id} type="button" data-testid={`corr-mention-${p.id}`} onMouseDown={(e) => { e.preventDefault(); pickMention(p); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-right hover:bg-slate-50">
                <Avatar name={p.name} size="h-8 w-8" /><span className="flex-1 truncate text-sm">{p.name}</span>
                <span className="text-[10px] text-slate-400">{p.kind === "employee" ? "موظف" : p.kind === "supervisor" ? "مشرف" : "الإدارة"}</span>
              </button>))}
          </div>
        )}
        {up.items.length > 0 && <div className="flex flex-wrap gap-1.5">{up.items.map((a) => (
          <span key={a.id} className="inline-flex items-center gap-1 rounded-lg bg-white px-2 py-1 text-xs"><span className="max-w-[140px] truncate">{a.name}</span>
            <button onClick={() => up.remove(a.id)}><X className="h-3 w-3" /></button></span>))}</div>}
        {actAs && parts.some((p) => p.id !== me) && <ActAsBar parts={parts.filter((p) => p.id !== me)} value={asParty} onChange={setAsParty} />}
        <div className="flex items-end gap-1.5">
          <button type="button" onClick={up.pick} disabled={up.busy} data-testid="corr-reply-attach" className="rounded-full p-2.5 text-slate-500 hover:bg-slate-200">
            {up.busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}</button>
          {up.node}
          {group && <button type="button" title="منشن" data-testid="corr-mention-btn" onClick={() => { setText((t) => `${t}${t && !t.endsWith(" ") ? " " : ""}@`); setMq(""); taRef.current?.focus(); }} className="rounded-full p-2.5 text-slate-500 hover:bg-slate-200"><AtSign className="h-5 w-5" /></button>}
          <textarea ref={taRef} data-testid="corr-reply-input" rows={1} value={text} onChange={onType}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (cands.length) pickMention(cands[0]); else send(); } if (e.key === "Escape") setMq(null); }}
            placeholder={asParty ? `${parts.find((p) => p.id === asParty)?.name || ""}…` : "اكتب رسالة… (@ للإشارة)"} className={`max-h-32 min-h-[42px] flex-1 resize-none rounded-3xl border-0 bg-white px-4 py-2.5 text-[15px] outline-none focus:ring-2 ${asParty ? "ring-2 ring-indigo-400 focus:ring-indigo-400" : "focus:ring-emerald-300"}`} />
          <button onClick={send} disabled={sending || up.busy} data-testid="corr-reply-send"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow transition hover:bg-emerald-700 active:scale-95 disabled:opacity-60">
            {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 -scale-x-100" />}</button>
        </div>
      </div>
    </div>
  );
};

export const ThreadRow = ({ t, active, onClick, right }) => (
  <button onClick={onClick} data-testid={`corr-thread-${t.id}`}
    className={`flex w-full items-center gap-3 px-3 py-2.5 text-right transition hover:bg-slate-50 ${active ? "bg-slate-100" : ""}`}>
    <Avatar name={t.participants_label || t.subject} group={!t.direct} />
    <div className="min-w-0 flex-1 border-b border-slate-100 pb-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-semibold text-slate-900">{t.direct ? t.participants_label : t.subject}</span>
        <span className={`shrink-0 text-[11px] ${t.unread ? "font-bold text-emerald-600" : "text-slate-400"}`}>{listTime(t.last_message_at)}</span>
      </div>
      <div className="mt-0.5 flex items-center justify-between gap-2">
        <span className="truncate text-[13px] text-slate-500">{t.last_sender_name && !t.direct ? `${t.last_sender_name}: ` : ""}{t.last_message_preview || "—"}</span>
        <span className="flex shrink-0 items-center gap-1">{right}
          {t.unread > 0 && <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[11px] font-bold text-white" data-testid={`corr-unread-${t.id}`}>{t.unread}</span>}</span>
      </div>
    </div>
  </button>
);
